import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Portal, Button, Confirm } from 'semantic-ui-react';

import {
    closeWorkspaceOverlay,
    featureLoaded,
    setClosedClicked,
    setContextMenuVisibility,
    setupGenericFeatureState,
} from 'common/store/workspaces/actions';
import { refreshMissingProperties } from 'common/store/context/actions';
import { ContextPropertyNames } from 'common/features/featureProperties';

import { WorkspacesState } from 'common/store/workspaces/reducer';
import {
    WorkspaceOverlaySizeOptions,
    WorkspacesOverlayTargets,
    WorkspacesOverlayTypes,
} from 'common/store/workspaces/types';
import { FeatureNamespace, getFeatureNamespace } from 'common/services/featureService';
import FeatureLoader from 'common/features/FeatureLoader';
import ColumnPicker from 'common/features/table/components/columnPicker/ColumnPicker';

import { OverlayFrame } from 'common/components/overlayFrame/FeatureOverlay';
import FeatureHeader from 'common/features/FeatureHeader';

import sectionLayoutService from 'author/src/features/structureEditor/services/SectionLayoutService';
import { SectionLayout } from 'author/src/store/context/types';
import EditRubricsOverlay from 'author/src/features/rubricOverlay/components/EditRubricsOverlay';
import RubricsTemplatesListModal from 'author/src/features/rubricOverlay/components/RubricsTemplatesListModal';
import ThresholdScoringOverlay from 'author/src/features/thresholdScoring/components/ThresholdScoringOverlay';
import * as styles from './WorkspaceOverlay.scss';
import { CommonRootState } from '../../store/typings';
import { CommonContextState } from '../../store/context/types';
import { ActionStatusService } from '../contextMenu/interfaces';
import { tl } from '../../services/i18nService';
import { AWSRumCustomEvents } from '../../store/common/constants';
import { recordCustomEvent } from '../../util/awsRumUtils';
import { STIMULUS_FEATURE_ID } from '../../util/utils';

interface OwnProps {
    appStore: any;
    urlContextParametersService?: any;
    actionStatusService: ActionStatusService;
}

interface WorkspaceOverlayAttributes {
    context: CommonContextState;
    workspaces: WorkspacesState;
    language: string;
}

interface WorkspaceOverlayDispatch {
    featureLoaded: (featureId: string) => void;
    closeWorkspaceOverlay: () => void;
    setClosedClicked: (payload: boolean) => void;
    setupGenericFeatureState: (featureId: string) => void;
    setContextMenuVisibility: (featureId: string) => void;
    refreshMissingProperties: (id: number, missingProperties: ContextPropertyNames) => void;
}

type WorkspaceOverlayProps = WorkspaceOverlayAttributes & WorkspaceOverlayDispatch & OwnProps;
type WorkspaceOverlayState = {
    confirmOpen: boolean;
};

class WorkspaceOverlay extends React.Component<WorkspaceOverlayProps, WorkspaceOverlayState> {
    wrapperElement: React.RefObject<any>;
    constructor(props: WorkspaceOverlayProps) {
        super(props);
        this.wrapperElement = React.createRef();
        this.state = {
            confirmOpen: false,
        };
    }

    isFeatureLoading = (featureId: string) => {
        const { featuresLoading } = this.props.workspaces;
        return featuresLoading.some((feature) => feature === featureId);
    };

    onGetGenericFeatureState = (featureId: string) => {
        const { genericFeatureState } = this.props.workspaces;
        return genericFeatureState[featureId] || genericFeatureState.default;
    };

    componentDidMount(): void {
        const { workspaces } = this.props;
        if (this.props.workspaces.workspaceOverlay.workspaceOverlayConfiguration?.context) {
            this.updateContextProperties();
        }
        this.wrapperElement.current.focus();

        if (workspaces.workspaceOverlay.workspaceOverlayName === WorkspacesOverlayTypes.DYNAMIC_ANSWER_KEYS) {
            recordCustomEvent(AWSRumCustomEvents.TOGGLE_DAK_OVERLAY, 'DAK overlay opened');
        }
    }

    componentDidUpdate(prevProps: WorkspaceOverlayProps) {
        const { workspaces } = this.props;
        const { workspaceOverlay, featuresLoading } = workspaces;
        const prevListOfFeaturesLoading = prevProps.workspaces.featuresLoading;
        const { workspaceOverlayConfiguration: { onReady, options } } = workspaceOverlay;
        const featureId = this.getFeatureId();
        const featureIsLoading = (feature: string) => feature === featureId;
        const featureWasLoading: boolean = prevListOfFeaturesLoading.some(featureIsLoading);
        const featureIsNotLoading = !featuresLoading.some(featureIsLoading);

        if (featureWasLoading && featureIsNotLoading && typeof onReady === 'function') {
            onReady();
        }

        if (options.onCloseWithWarning) {
            document.addEventListener('keydown', this.onEscape, false);
        }
    }

    componentWillUnmount() {
        const { workspaces } = this.props;
        if (workspaces.workspaceOverlay.workspaceOverlayName === WorkspacesOverlayTypes.DYNAMIC_ANSWER_KEYS) {
            recordCustomEvent(AWSRumCustomEvents.TOGGLE_DAK_OVERLAY, 'DAK overlay closed');
        }
    }

    onEscape = (e: any) => {
        const { workspaces: { workspaceOverlay: { workspaceOverlayConfiguration: { options } } } } = this.props;
        if (e.key === 'Escape') {
            if (options.isClosedClicked) {
                this.onClose();
            } else {
                this.onWarning();
            }
        }
    }

    updateContextProperties(): void {
        const {
            workspaces, context, refreshMissingProperties, urlContextParametersService,
        } = this.props;
        const { workspaceOverlay } = workspaces;
        const { workspaceOverlayConfiguration } = workspaceOverlay;
        const overlayContext = workspaceOverlayConfiguration.context;

        if (overlayContext && overlayContext.partyId
            && context.partyId !== overlayContext.partyId) {
            urlContextParametersService.setPartyId(overlayContext.partyId);
            refreshMissingProperties(overlayContext.partyId, ['partyState']);
        }

        if (overlayContext && overlayContext.questionId
            && context.questionId !== overlayContext.questionId) {
            urlContextParametersService.setQuestionId(overlayContext.questionId);
            refreshMissingProperties(overlayContext.questionId, ['questionState']);
        }
    }

    onCloseWithCondition = () => {
        const { workspaces } = this.props;
        const { workspaceOverlay } = workspaces;
        const { workspaceOverlayConfiguration } = workspaceOverlay;
        const { options } = workspaceOverlayConfiguration;
        if (options.closeCondition()) {
            this.setState({
                confirmOpen: true,
            });
        } else {
            this.onClose();
        }
    }

    onClose = async () => {
        const { closeWorkspaceOverlay, workspaces } = this.props;
        const { workspaceOverlay } = workspaces;
        const { workspaceOverlayConfiguration, workspaceOverlayName } = workspaceOverlay;
        const { onClose } = workspaceOverlayConfiguration;
        if (onClose) {
            onClose();
        }
        if (workspaceOverlayName === WorkspacesOverlayTypes.ADD_STIMULUS_FROM_BANK) {
            await closeWorkspaceOverlay();
            await sectionLayoutService.openStimulusAndLayoutModal(STIMULUS_FEATURE_ID,
                undefined,
                {} as SectionLayout,
                true);
        } else {
            closeWorkspaceOverlay();
        }
    };

    onCancelConfirm = () => {
        this.setState({
            confirmOpen: false,
        });
    }

    onWarning = () => {
        const { setClosedClicked, workspaces: { workspaceOverlay: { workspaceOverlayConfiguration: { options } } } } = this.props;
        if (options.hasBeenEdited) {
            setClosedClicked(true);
        } else {
            this.onClose();
        }
    }


    getFeatureId = () => {
        const { workspaces, context } = this.props;
        const { workspaceOverlay } = workspaces;
        if (workspaceOverlay) {
            const { workspaceOverlayName } = workspaceOverlay;
            // overlay is independent from workspaces, such as committeeSelector Overlay, Workflow overly
            if (workspaceOverlay.targetNodeId === WorkspacesOverlayTargets.APP_OVERLAY_TARGET) {
                return `feature_${workspaceOverlayName}_overlay`;
            }

            return `feature_${workspaceOverlayName}_${context.workspaceId}_overlay`;
        }
        return null;
    };

    renderOverlayContent = () => {
        // TODO: each overlay should have different help link
        const featureDefaultOptions = { hideContextMenu: true, fullHeight: true };
        const {
            appStore, context, setupGenericFeatureState,
            workspaces, language, featureLoaded, actionStatusService,
        } = this.props;
        const { workspaceOverlay } = workspaces;
        const { workspaceOverlayName, workspaceOverlayConfiguration } = workspaceOverlay;
        const { options } = workspaceOverlayConfiguration;
        const customContext = {
            ...context,
            ...workspaceOverlayConfiguration.context,
        };
        const featureId = this.getFeatureId();
        switch (workspaceOverlayName) {
        case WorkspacesOverlayTypes.EDIT_RUBRICS_OVERLAY:
            return (
                <EditRubricsOverlay {...workspaceOverlayConfiguration.options} />
            );
        case WorkspacesOverlayTypes.RUBRICS_TEMPLATES_LIST_MODAL:
            return (
                <RubricsTemplatesListModal {...workspaceOverlayConfiguration.options} />
            );
        case WorkspacesOverlayTypes.THRESHOLD_SCORING_OVERLAY:
            return (
                <ThresholdScoringOverlay {...workspaceOverlayConfiguration.options} />
            );
        case WorkspacesOverlayTypes.COLUMN_PICKER:
            return (
                <ColumnPicker
                    defaultColumns={options.defaultColumns}
                    featureId={options.tableFeatureId}
                />
            );
        default:
            return (
                <FeatureLoader
                    FeatureHeader={FeatureHeader}
                    featureName={workspaceOverlayName}
                    options={{ ...options, ...featureDefaultOptions }}
                    context={customContext}
                    setFeatureLoaded={featureLoaded}
                    isFeatureLoading={this.isFeatureLoading}
                    getGenericFeatureState={this.onGetGenericFeatureState}
                    setupGenericFeatureState={setupGenericFeatureState}
                    activeOverlay={undefined}
                    featureId={featureId}
                    language={language}
                    footerMode={false}
                    appStore={appStore}
                    featureNamespace={getFeatureNamespace(
                        workspaceOverlayName,
                        APP_NAME as FeatureNamespace
                    )}
                    actionStatusService={actionStatusService}
                />
            );
        }
    };

    handleOnClose = () => {
        const { workspaces } = this.props;
        const { workspaceOverlay } = workspaces;
        const { workspaceOverlayConfiguration: { options } } = workspaceOverlay;
        if (typeof options.closeCondition === 'function') {
            return this.onCloseWithCondition();
        } if (options.onCloseWithWarning) {
            return this.onWarning();
        }
        return this.onClose();
    }

    render() {
        const { workspaces } = this.props;
        const { confirmOpen } = this.state;
        const { workspaceOverlay } = workspaces;
        const { workspaceOverlayConfiguration, targetNodeId } = workspaceOverlay;
        const {
            workspaceOverlaySize,
            workspaceTitle,
            workspaceSecondaryTitle,
            options,
        } = workspaceOverlayConfiguration;
        const overlayNode = document.getElementById(targetNodeId);
        if (!overlayNode) {
            return null;
        }

        const isMidsizedOverlay = workspaceOverlaySize === WorkspaceOverlaySizeOptions.MIDSIZED_CENTERED_OVERLAY;
        let animationClass = '';
        if (options.removeAnimationClasses) {
            animationClass = styles.noAnimation;
        }
        const mainClass = isMidsizedOverlay
            ? styles.overlayMidsized
            : `${styles.overlay} ${animationClass}`;
        const wrapperClass = `${mainClass} ${!(options && options.withoutHeader) ? styles.padding : ''}`;

        return (
            <Portal
                open={!!workspaceOverlay}
                mountNode={overlayNode}
                closeOnEscape={!options.onCloseWithWarning}
                closeOnDocumentClick={false}
                onClose={this.onClose}
            >
                <div
                    className={isMidsizedOverlay ? wrapperClass : ''}
                >
                    <div
                        className={isMidsizedOverlay ? styles.overlayContentWrapper : wrapperClass}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="overlayTitle"
                        tabIndex={-1}
                        ref={this.wrapperElement}
                    >
                        {!(options && options.withoutHeader)
                            ? (
                                <OverlayFrame
                                    onClose={this.handleOnClose}
                                    text={workspaceTitle}
                                    secondaryText={workspaceSecondaryTitle}
                                    isNotClosable={options.isNotClosable}
                                    iconTitle={options.iconTitle}
                                    actionButtonRight={options.actionButtonRight}
                                    isMidsizedOverlay={isMidsizedOverlay}
                                    backBtnComponent={options.backBtnComponent}
                                    dynamicOverlayTitle={options.dynamicOverlayTitle}
                                    withoutIcon={options.withoutIcon}
                                    closeButtonClasses={options.closeButtonClasses}
                                    removeAnimationClasses={options.removeAnimationClasses}
                                >
                                    {this.renderOverlayContent()}
                                </OverlayFrame>
                            )
                            : this.renderOverlayContent()}
                    </div>
                    <Confirm
                        open={confirmOpen}
                        cancelButton={
                            <Button>{tl('common.workspaceOverlay.confirmClose.cancel')}</Button>
                        }
                        confirmButton={
                            <Button>{tl('common.workspaceOverlay.confirmClose.confirm')}</Button>
                        }
                        header={tl('common.workspaceOverlay.confirmClose.header')}
                        content={tl('common.workspaceOverlay.confirmClose.content')}
                        onCancel={this.onCancelConfirm}
                        onConfirm={this.onClose}
                        closeOnEscape={false}
                    />
                </div>
            </Portal>
        );
    }
}

const mapStateToProps = (state: CommonRootState): WorkspaceOverlayAttributes => ({
    context: state.context,
    workspaces: state.workspaces,
    language: state.session.language,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
    {
        featureLoaded,
        closeWorkspaceOverlay,
        setupGenericFeatureState,
        setContextMenuVisibility,
        refreshMissingProperties,
        setClosedClicked,
    },
    dispatch,
);

export default connect<WorkspaceOverlayAttributes, WorkspaceOverlayDispatch>(mapStateToProps, mapDispatchToProps)(WorkspaceOverlay);
